import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: 'Bearer ' + token() });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';
// Non-admins granted staff-directory EDIT rights (by username). Deleting people +
// the Supervisor toggle stay admin-only. Mirrors the backend allowlist.
const DIRECTORY_EDITORS = ['glee'];

// Known org-chart departments → accent color. Everything else falls back to gray.
const DEPT_COLOR = {
  'EXECUTIVE': '#EA6F2E',
  'ADMINISTRATION': '#3b5ba5',
  'COLLECTIONS': '#2f8f6b',
  'PUBLIC ENGAGEMENT': '#7a4fa5',
  'EXHIBITIONS': '#a5533b',
};
const GRAY_CHIP = '#8A94A6';

// Directory data is stored ALL-CAPS; show it title-cased for a cleaner, professional look.
const titleCase = (s) => (s ? String(s).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\bIi\b/g, 'II').replace(/\bIii\b/g, 'III').replace(/\bCaam\b/g, 'CAAM') : '');

// Resolve the department to show: prefer the clean org-chart department (Executive,
// Administration, …) and fall back to the free-text staff.department. Known org-chart
// departments get their accent color; anything else is gray. Returns null only when
// there is genuinely no department on record.
const deptMeta = (person) => {
  const raw = (person.org_department || person.department || '').trim();
  if (!raw) return null;
  // Only case that isn't worth showing: a free-text department that's an exact copy
  // of the title (org-chart departments are always distinct, so they always show).
  if (!person.org_department && person.title && raw.toUpperCase() === String(person.title).trim().toUpperCase()) return null;
  const key = raw.toUpperCase();
  return { label: titleCase(raw), color: DEPT_COLOR[key] || GRAY_CHIP };
};

// Comparable value for a person by list-column key (used for sortable headers).
const sortValue = (p, key) => {
  switch (key) {
    case 'name':        return `${p.last_name || ''} ${p.first_name || ''}`.trim().toLowerCase();
    case 'title':       return (p.title || '').toLowerCase();
    case 'department':  { const d = deptMeta(p); return d ? d.label.toLowerCase() : ''; }
    case 'email':       return (p.email || '').toLowerCase();
    case 'extension':   return (p.extension || '').toLowerCase();
    case 'cell_phone':  return (p.cell_phone || '').toLowerCase();
    default:            return '';
  }
};

const BTN = ({ onClick, children, variant='default', style={}, disabled=false }) => {
  const base = { display:'inline-flex', alignItems:'center', gap:'6px', padding:'8px 18px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor: disabled ? 'not-allowed' : 'pointer', border:'1px solid', opacity: disabled ? 0.5 : 1, transition:'all 0.15s' };
  const variants = {
    default: { background:'#fff', borderColor:'#E5E7EB', color:'#374151' },
    primary: { background: NAVY, borderColor: NAVY, color:'#fff' },
    orange:  { background: ORANGE, borderColor: ORANGE, color:'#fff' },
    danger:  { background:'#EF4444', borderColor:'#EF4444', color:'#fff' },
  };
  return <button onClick={onClick} disabled={disabled} style={{...base, ...variants[variant], ...style}}>{children}</button>;
};

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, []);
  return (
    <div style={{ position:'fixed', bottom:'32px', left:'50%', transform:'translateX(-50%)', zIndex:9999, background:'#111827', color:'#fff', padding:'13px 24px', borderRadius:'10px', boxShadow:'0 8px 32px rgba(0,0,0,0.3)', fontSize:'13px', fontWeight:'500' }}>
      {message}
    </div>
  );
}

function AddStaffModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', title: '', department: '', extension: '', cell_phone: '', username: '', password: ''
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const inputStyle = { width:'100%', padding:'9px 11px', border:'1px solid #E5E7EB', borderRadius:'6px', fontSize:'13px', outline:'none' };
  const labelStyle = { display:'block', fontSize:'11px', fontWeight:'600', color:'#6B7280', marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.04em' };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.username || !form.password) {
      alert('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      alert('Failed to add staff: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:'12px', padding:'28px', width:'600px', maxWidth:'90vw', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
          <h2 style={{ fontSize:'18px', fontWeight:'700', color:'#111827', margin:0 }}>Add New Staff Member</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:'22px' }}>×</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'20px' }}>
          <div>
            <label style={labelStyle}>First Name *</label>
            <input type="text" value={form.first_name} onChange={e => set('first_name', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Last Name *</label>
            <input type="text" value={form.last_name} onChange={e => set('last_name', e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <label style={labelStyle}>Email *</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Username *</label>
            <input type="text" value={form.username} onChange={e => set('username', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Password *</label>
            <input type="password" value={form.password} onChange={e => set('password', e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <label style={labelStyle}>Title</label>
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <label style={labelStyle}>Department</label>
            <input type="text" value={form.department} onChange={e => set('department', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Extension</label>
            <input type="text" value={form.extension} onChange={e => set('extension', e.target.value)} style={inputStyle} placeholder="e.g. 744-2066" />
          </div>
          <div>
            <label style={labelStyle}>Cell Phone</label>
            <input type="text" value={form.cell_phone} onChange={e => set('cell_phone', e.target.value)} style={inputStyle} placeholder="e.g. (213) 555-1234" />
          </div>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px' }}>
          <BTN onClick={onClose}>Cancel</BTN>
          <BTN variant="orange" onClick={handleSave} disabled={saving}>{saving ? 'Adding...' : 'Add Staff Member'}</BTN>
        </div>
      </div>
    </div>
  );
}

function EditModal({ staff, onClose, onSave, isAdmin }) {
  const [form, setForm] = useState({
    first_name: staff.first_name || '',
    last_name: staff.last_name || '',
    title: staff.title || '',
    department: staff.department || '',
    extension: staff.extension || '',
    cell_phone: staff.cell_phone || '',
    is_supervisor: !!staff.is_supervisor
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const inputStyle = { width:'100%', padding:'9px 11px', border:'1px solid #E5E7EB', borderRadius:'6px', fontSize:'13px', outline:'none' };
  const labelStyle = { display:'block', fontSize:'11px', fontWeight:'600', color:'#6B7280', marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.04em' };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(staff.id, form);
      onClose();
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:'12px', padding:'28px', width:'500px', maxWidth:'90vw' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
          <h2 style={{ fontSize:'18px', fontWeight:'700', color:'#111827', margin:0 }}>Edit Staff Member</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:'22px' }}>×</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'20px' }}>
          <div>
            <label style={labelStyle}>First Name</label>
            <input type="text" value={form.first_name} onChange={e => set('first_name', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Last Name</label>
            <input type="text" value={form.last_name} onChange={e => set('last_name', e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <label style={labelStyle}>Title</label>
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <label style={labelStyle}>Department</label>
            <input type="text" value={form.department} onChange={e => set('department', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Extension</label>
            <input type="text" value={form.extension} onChange={e => set('extension', e.target.value)} style={inputStyle} placeholder="e.g. 744-2066" />
          </div>
          <div>
            <label style={labelStyle}>Cell Phone</label>
            <input type="text" value={form.cell_phone} onChange={e => set('cell_phone', e.target.value)} style={inputStyle} placeholder="e.g. (213) 555-1234" />
          </div>
          {isAdmin && (
            <div style={{ gridColumn:'1 / -1', display:'flex', alignItems:'center', gap:'10px', padding:'12px 14px', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:'8px' }}>
              <input
                id="is_supervisor"
                type="checkbox"
                checked={form.is_supervisor}
                onChange={e => set('is_supervisor', e.target.checked)}
                style={{ width:'18px', height:'18px', cursor:'pointer', accentColor: ORANGE }}
              />
              <label htmlFor="is_supervisor" style={{ cursor:'pointer', margin:0 }}>
                <span style={{ display:'block', fontSize:'13px', fontWeight:600, color:'#111827' }}>Supervisor</span>
                <span style={{ display:'block', fontSize:'11px', color:'#6B7280' }}>Can be selected as a supervisor in onboarding &amp; time-off routing</span>
              </label>
            </div>
          )}
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px' }}>
          <BTN onClick={onClose}>Cancel</BTN>
          <BTN variant="orange" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</BTN>
        </div>
      </div>
    </div>
  );
}

export default function StaffDirectory({ currentUser }) {
  const isDirAdmin = currentUser?.role === 'admin';
  const canEditDir = isDirAdmin || DIRECTORY_EDITORS.includes(currentUser?.username);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingStaff, setEditingStaff] = useState(null);
  const [addingStaff, setAddingStaff] = useState(false);
  const [toast, setToast] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('staffDirView') || 'cards'); // 'cards' | 'list'
  const changeView = (v) => { setViewMode(v); try { localStorage.setItem('staffDirView', v); } catch (e) {} };
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' }); // list-view column sort
  const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + '/staff-directory', { 
        headers: headers(),
        params: { search, filter }
      });
      setStaff(res.data);
    } catch (e) {
      console.error(e);
      setToast('Failed to load staff directory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    const timer = setTimeout(() => fetchStaff(), 300);
    return () => clearTimeout(timer);
  }, [search, filter]);

  const handleAddStaff = async (data) => {
    await axios.post(API + '/auth/register', data, { headers: headers() });
    fetchStaff();
    setToast('Staff member added successfully!');
  };

  const handlePrintPDF = () => {
    const printWindow = window.open('', '_blank');
    const logoUrl = `${window.location.origin}/caam-logo.png`;
    const asOf = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));

    // Group by department: known org-chart depts first (fixed order), then any other
    // non-blank departments alphabetically, then "Unassigned".
    const KNOWN_ORDER = ['EXECUTIVE', 'ADMINISTRATION', 'COLLECTIONS', 'PUBLIC ENGAGEMENT', 'EXHIBITIONS'];
    const groups = {};
    staff.forEach(p => {
      const deptRaw = (p.org_department || p.department || '').trim();
      const key = deptRaw ? deptRaw.toUpperCase() : 'UNASSIGNED';
      (groups[key] = groups[key] || []).push(p);
    });
    const orderedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'UNASSIGNED') return 1;
      if (b === 'UNASSIGNED') return -1;
      const ia = KNOWN_ORDER.indexOf(a), ib = KNOWN_ORDER.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.localeCompare(b);
    });
    const rows = orderedKeys.map(key => {
      const label = key === 'UNASSIGNED' ? 'Unassigned' : titleCase(key);
      const people = [...groups[key]].sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
      return `<tr class="deptband"><td colspan="5">${esc(label)}</td></tr>` +
        people.map(p => `<tr>
          <td class="nm">${esc(p.first_name)} ${esc(p.last_name)}</td>
          <td>${esc(titleCase(p.title)) || '—'}</td>
          <td>${esc(p.extension) || '—'}</td>
          <td>${esc(p.email) || '—'}</td>
          <td>${esc(p.cell_phone) || '—'}</td>
        </tr>`).join('');
    }).join('');

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>CAAM Staff Directory</title>
<style>
  @page { margin: 0.6in; }
  * { box-sizing: border-box; }
  body { font-family: Helvetica, Arial, sans-serif; margin: 0; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .ph { display: flex; align-items: center; gap: 16px; border-bottom: 2.5px solid ${NAVY}; padding-bottom: 14px; }
  .ph img { height: 52px; }
  .ph .org { font-size: 17px; font-weight: 800; color: ${NAVY}; letter-spacing: .01em; }
  .ph .sub { font-size: 11px; color: #555; margin-top: 3px; }
  .ph .right { margin-left: auto; text-align: right; font-size: 10.5px; color: #555; line-height: 1.5; }
  .ph .right b { color: ${NAVY}; }
  .doc-title { font-size: 12.5px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; color: ${NAVY}; margin: 18px 0 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  thead { display: table-header-group; }
  thead th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: .05em; color: #fff; background: ${NAVY}; padding: 6px 9px; }
  tbody td { padding: 5px 9px; border-bottom: .5px solid #dfe3e8; color: #222; }
  tbody td.nm { font-weight: 700; color: ${NAVY}; white-space: nowrap; }
  tbody tr { break-inside: avoid; }
  tbody tr:nth-child(even) td { background: #f7f9fb; }
  .deptband td { background: ${NAVY} !important; color: #fff; font-weight: 700; font-size: 9.5px; letter-spacing: .06em; text-transform: uppercase; padding: 6px 9px; }
  .pf { margin-top: 18px; border-top: 1px solid #dfe3e8; padding-top: 8px; display: flex; justify-content: space-between; font-size: 8.5px; color: #888; }
</style></head>
<body onload="setTimeout(function(){window.print();},250)">
  <div class="ph">
    <img src="${logoUrl}" alt="CAAM" onerror="this.style.display='none'">
    <div>
      <div class="org">California African American Museum</div>
      <div class="sub">600 State Drive, Exposition Park &middot; Los Angeles, CA 90037-1267</div>
    </div>
    <div class="right"><b>Staff Directory</b><br>T 213-744-7432 &middot; F 213-744-2050<br>As of ${asOf}</div>
  </div>
  <div class="doc-title">Contact Directory</div>
  <table>
    <thead><tr><th style="width:24%">Name</th><th style="width:30%">Title</th><th style="width:10%">Ext</th><th style="width:21%">Email</th><th style="width:15%">Mobile</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="pf"><span>California African American Museum &mdash; Internal Staff Directory</span><span>Confidential</span></div>
</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handlePhotoUpload = async (staffId, file) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      
      await axios.post(API + '/staff-directory/' + staffId + '/photo', formData, { 
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' }
      });
      
      fetchStaff();
      setToast('Photo uploaded successfully!');
    } catch (e) {
      alert('Failed to upload photo: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleEdit = async (staffId, data) => {
    await axios.put(API + '/staff-directory/' + staffId, data, { headers: headers() });
    fetchStaff();
    setToast('Staff member updated!');
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('Are you sure you want to remove this staff member from the directory?')) return;
    try {
      await axios.delete(API + '/staff-directory/' + staffId, { headers: headers() });
      fetchStaff();
      setToast('Staff member removed');
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.error || e.message));
    }
  };

  const getInitials = (firstName, lastName) => {
    return ((firstName?.charAt(0) || '') + (lastName?.charAt(0) || '')).toUpperCase();
  };

  if (loading) return <div style={{ padding:'40px', textAlign:'center', color:'#9CA3AF' }}>Loading...</div>;

  // List view: sort by the active column; blanks always sort to the bottom.
  const listStaff = [...staff].sort((a, b) => {
    const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
    if (!va && vb) return 1;
    if (va && !vb) return -1;
    const cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  return (
    <div style={{ padding:'24px 28px' }}>
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      {editingStaff && <EditModal staff={editingStaff} onClose={() => setEditingStaff(null)} onSave={handleEdit} isAdmin={currentUser?.role === 'admin'} />}
      {addingStaff && <AddStaffModal onClose={() => setAddingStaff(false)} onSave={handleAddStaff} />}

      <div style={{ background:'#fff', padding:'24px 28px', borderRadius:'14px', marginBottom:'24px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <h2 style={{ fontSize:'22px', fontWeight:'600', color: NAVY, margin:0 }}>Staff Directory</h2>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <div style={{ display:'inline-flex', border:'1px solid #E5E5E7', borderRadius:'9px', overflow:'hidden', marginRight:'2px' }}>
              {[['cards','Cards','M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z'],['list','List','M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01']].map(([mode, label, path]) => (
                <button key={mode} onClick={() => changeView(mode)}
                  style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'9px 13px', border:'none', background: viewMode===mode ? NAVY : '#fff', color: viewMode===mode ? '#fff' : '#6B7280', fontSize:'13px', fontWeight:'700', cursor:'pointer', transition:'all 0.15s' }}>
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d={path}/></svg>
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={handlePrintPDF}
              style={{ display:'flex', alignItems:'center', gap:'6px', padding:'10px 18px', background: NAVY, color:'#fff', border:'none', borderRadius:'8px', fontSize:'14px', fontWeight:'600', cursor:'pointer', transition:'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#0F2A4C'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(26,58,92,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = NAVY; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
              </svg>
              Print PDF
            </button>
            <button 
              onClick={() => setAddingStaff(true)}
              style={{ display:'flex', alignItems:'center', gap:'6px', padding:'10px 18px', background: ORANGE, color:'#fff', border:'none', borderRadius:'8px', fontSize:'14px', fontWeight:'600', cursor:'pointer', transition:'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#D85F1E'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(234,111,46,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = ORANGE; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
              Add Staff
            </button>
          </div>
        </div>

        <div style={{ position:'relative', marginBottom:'16px' }}>
          <svg style={{ position:'absolute', left:'16px', top:'50%', transform:'translateY(-50%)', width:'18px', height:'18px', opacity:0.4 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input 
            type="text" 
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, department, or email..."
            style={{ width:'100%', padding:'12px 16px 12px 44px', border:'1.5px solid #E5E5E7', borderRadius:'10px', fontSize:'15px', outline:'none', transition:'all 0.2s' }}
            onFocus={e => { e.target.style.borderColor = ORANGE; e.target.style.boxShadow = '0 0 0 3px rgba(234,111,46,0.1)'; }}
            onBlur={e => { e.target.style.borderColor = '#E5E5E7'; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          {['all', 'a-e', 'f-j', 'k-o', 'p-t', 'u-z'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding:'8px 16px',
                border:'1.5px solid ' + (filter === f ? ORANGE : '#E5E5E7'),
                background: filter === f ? ORANGE : '#fff',
                color: filter === f ? '#fff' : '#1D1D1F',
                borderRadius:'8px',
                fontSize:'13px',
                fontWeight:'600',
                cursor:'pointer',
                transition:'all 0.2s',
                textTransform:'uppercase'
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {staff.length === 0 ? (
        <div style={{ textAlign:'center', padding:'80px 20px', color:'#9CA3AF' }}>
          <p style={{ fontSize:'14px', fontWeight:'600', color:'#6B7280' }}>No staff members found</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'16px' }}>
          {staff.map(person => {
            const dept = deptMeta(person);
            const photoSrc = person.google_photo_url ? person.google_photo_url : person.photo_url ? `${API}${person.photo_url}` : null;
            const isAdmin = currentUser?.role === 'admin';
            return (
            <div key={person.id}
              style={{ position:'relative', background:'#fff', border:'1px solid #e6e9ef', borderRadius:'14px', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.05)', transition:'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 20px rgba(0,0,0,0.09)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.05)'; }}
            >
              <div style={{ height:'4px', background: dept ? dept.color : '#e6e9ef' }} />
              {canEditDir && (
                <div style={{ position:'absolute', top:'14px', right:'12px', display:'flex', gap:'6px', opacity:0, transition:'opacity 0.2s' }} className="card-actions">
                  <button onClick={() => setEditingStaff(person)} title="Edit"
                    style={{ width:'30px', height:'30px', borderRadius:'7px', border:'none', background:'#F5F5F7', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                    onMouseEnter={e => { e.currentTarget.style.background='#FEF3C7'; e.currentTarget.style.color='#92400E'; }}
                    onMouseLeave={e => { e.currentTarget.style.background='#F5F5F7'; e.currentTarget.style.color='inherit'; }}>
                    <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                  </button>
                  {isDirAdmin && (
                  <button onClick={() => handleDelete(person.id)} title="Delete"
                    style={{ width:'30px', height:'30px', borderRadius:'7px', border:'none', background:'#F5F5F7', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                    onMouseEnter={e => { e.currentTarget.style.background='#FEE2E2'; e.currentTarget.style.color='#991B1B'; }}
                    onMouseLeave={e => { e.currentTarget.style.background='#F5F5F7'; e.currentTarget.style.color='inherit'; }}>
                    <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                  </button>
                  )}
                </div>
              )}
              <div style={{ padding:'16px 18px' }}>
                <div style={{ display:'flex', gap:'13px', alignItems:'center' }}>
                  <div style={{ position:'relative', flexShrink:0 }}>
                    <input type="file" accept="image/*" onChange={e => e.target.files[0] && handlePhotoUpload(person.id, e.target.files[0])}
                      style={{ position:'absolute', inset:0, opacity:0, cursor:'pointer', width:'54px', height:'54px', borderRadius:'50%', zIndex:2 }} title="Click to upload photo" />
                    <div style={{ width:'54px', height:'54px', borderRadius:'50%', background: photoSrc ? '#fff' : 'linear-gradient(135deg,#1A3A5C,#2A4A6C)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'17px', fontWeight:700, color:'#fff', overflow:'hidden', border:'2px solid #fff', boxShadow:'0 0 0 1px #e6e9ef' }}>
                      {photoSrc ? <img src={photoSrc} alt={person.first_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} referrerPolicy="no-referrer" /> : getInitials(person.first_name, person.last_name)}
                    </div>
                  </div>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontSize:'15.5px', fontWeight:700, color: NAVY, lineHeight:1.2, display:'flex', alignItems:'center', gap:'7px', flexWrap:'wrap' }}>
                      {person.first_name} {person.last_name}
                      {person.is_supervisor && <span style={{ fontSize:'8.5px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', color:'#047857', background:'#ECFDF5', border:'1px solid #A7F3D0', borderRadius:'999px', padding:'1px 6px' }}>Supervisor</span>}
                    </div>
                    <div style={{ fontSize:'12px', color:'#6b7280', marginTop:'2px', lineHeight:1.3 }}>{titleCase(person.title) || 'Staff Member'}</div>
                    {dept && <span style={{ display:'inline-block', marginTop:'9px', fontSize:'9.5px', fontWeight:700, letterSpacing:'0.04em', textTransform:'uppercase', color:'#fff', background: dept.color, borderRadius:'6px', padding:'2px 8px' }}>{dept.label}</span>}
                  </div>
                </div>
                {(person.email || person.extension || person.cell_phone) && (
                  <div style={{ marginTop:'14px', paddingTop:'13px', borderTop:'1px solid #f1f3f6', display:'flex', flexDirection:'column', gap:'8px' }}>
                    {person.email && (
                      <div style={{ display:'flex', alignItems:'center', gap:'9px', fontSize:'12.5px', color:'#33415c', minWidth:0 }}>
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ opacity:0.45, flexShrink:0 }}><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                        <a href={`mailto:${person.email}`} style={{ color:'#33415c', textDecoration:'none', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{person.email}</a>
                      </div>
                    )}
                    {(person.extension || person.cell_phone) && (
                      <div style={{ display:'flex', alignItems:'center', gap:'9px', fontSize:'12.5px', color:'#33415c' }}>
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ opacity:0.45, flexShrink:0 }}><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                        <span>
                          {person.extension && <><span style={{ color:'#9aa4b2' }}>Ext</span> {person.extension}</>}
                          {person.extension && person.cell_phone && <span style={{ color:'#c9d2df', margin:'0 6px' }}>•</span>}
                          {person.cell_phone && <><span style={{ color:'#9aa4b2' }}>Cell</span> {person.cell_phone}</>}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background:'#fff', border:'1px solid #e6e9ef', borderRadius:'14px', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
            <thead>
              <tr>
                {[['Name','name'],['Title','title'],['Department','department'],['Email','email'],['Ext','extension'],['Mobile','cell_phone']].map(([label, key]) => (
                  <th key={key} onClick={() => toggleSort(key)}
                    style={{ background: NAVY, color:'#fff', textAlign:'left', fontSize:'11px', fontWeight:700, letterSpacing:'0.04em', textTransform:'uppercase', padding:'11px 14px', cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}
                    title={`Sort by ${label}`}>
                    {label}
                    <span style={{ marginLeft:'6px', fontSize:'9px', opacity: sort.key === key ? 1 : 0.4 }}>{sort.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                  </th>
                ))}
                {canEditDir && <th style={{ background: NAVY, width:'72px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {listStaff.map(person => {
                const dept = deptMeta(person);
                const photoSrc = person.google_photo_url ? person.google_photo_url : person.photo_url ? `${API}${person.photo_url}` : null;
                return (
                <tr key={person.id} style={{ borderBottom:'1px solid #eef1f5' }}
                  onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'10px 14px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'10px', fontWeight:600, color: NAVY }}>
                      <div style={{ width:'30px', height:'30px', borderRadius:'50%', background: photoSrc ? '#fff' : 'linear-gradient(135deg,#1A3A5C,#2A4A6C)', color:'#fff', fontSize:'11px', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
                        {photoSrc ? <img src={photoSrc} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} referrerPolicy="no-referrer" /> : getInitials(person.first_name, person.last_name)}
                      </div>
                      {person.is_supervisor && <span title="Supervisor" style={{ width:'7px', height:'7px', borderRadius:'50%', background:'#10B981', flexShrink:0 }} />}
                      {person.first_name} {person.last_name}
                    </div>
                  </td>
                  <td style={{ padding:'10px 14px', color:'#4b5563' }}>{titleCase(person.title) || '—'}</td>
                  <td style={{ padding:'10px 14px' }}>{dept ? <span style={{ fontSize:'9px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.03em', color:'#fff', background: dept.color, borderRadius:'5px', padding:'2px 7px' }}>{dept.label}</span> : <span style={{ color:'#9aa4b2' }}>—</span>}</td>
                  <td style={{ padding:'10px 14px' }}>{person.email ? <a href={`mailto:${person.email}`} style={{ color: NAVY, textDecoration:'none' }}>{person.email}</a> : '—'}</td>
                  <td style={{ padding:'10px 14px', color:'#4b5563' }}>{person.extension || '—'}</td>
                  <td style={{ padding:'10px 14px', color:'#4b5563' }}>{person.cell_phone || '—'}</td>
                  {canEditDir && (
                    <td style={{ padding:'10px 14px', whiteSpace:'nowrap' }}>
                      <button onClick={() => setEditingStaff(person)} title="Edit" style={{ width:'28px', height:'28px', borderRadius:'6px', border:'none', background:'#F5F5F7', cursor:'pointer', marginRight:'4px' }}>
                        <svg width="14" height="14" fill="none" stroke="#92400E" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                      </button>
                      {isDirAdmin && (
                      <button onClick={() => handleDelete(person.id)} title="Delete" style={{ width:'28px', height:'28px', borderRadius:'6px', border:'none', background:'#F5F5F7', cursor:'pointer' }}>
                        <svg width="14" height="14" fill="none" stroke="#991B1B" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                      </button>
                      )}
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .card-actions { opacity: 0 !important; }
        div:hover > .card-actions { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
